import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import { stringify } from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Link from '@/components/switchback/Link/Link';
import {
  ModularListingTile,
  ModularListingTileContent,
  ModularListingTileContentShort,
  ModularListingTileMedia,
} from '@/components/ui/ModularListingTile';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getIsAuthenticated } from '@/redux/selectors/auth/user';
import {
  trackListingImageViewedEvent,
  trackListingSelectedEvent,
} from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import {
  trackRecommendationTabSelected,
  trackViewAllRentalsClicked,
} from '@/services/analytics/search';
import { ESearchSource } from '@/services/analytics/search/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

import css from './Recommendations.module.css';
import { ETagSlug, Tags } from './Tags';
import { ERecommendationsMode, useRecommendations } from './useRecommendations';

interface IRecommendationsProps {
  myIPAddress?: string;
  mode?: ERecommendationsMode;
}

export const Recommendations = ({
  myIPAddress,
  mode = ERecommendationsMode.RENTALS,
}: IRecommendationsProps) => {
  const { isAboveDesktop, isAboveTablet } = useBreakpoint();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const forYouFeedEnabled = useExperimentIsEnabled(OptimizelyFlags.HOMEPAGE_FOR_YOU_FEED);
  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const isDeliveryMode = mode === ERecommendationsMode.DELIVERY;

  const [activeTag, setActiveTag] = useState(ETagSlug.GuestFavorite);
  const recommendationsInitiallyTriggeredRef = useRef(false);

  const listingSource = isDeliveryMode
    ? EListingSource.DELIVERY_RECOMMENDATIONS
    : EListingSource.HOMEPAGE_RECOMMENDATIONS;

  const recommendations = useRecommendations(activeTag, listingSource, mode);

  useEffect(() => {
    // Only set the ForYou tag for rentals mode when authenticated and feature enabled
    if (!isDeliveryMode && forYouFeedEnabled && isAuthenticated) {
      setActiveTag(ETagSlug.ForYou);
    } else {
      setActiveTag(ETagSlug.GuestFavorite);
    }
  }, [forYouFeedEnabled, isAuthenticated, isDeliveryMode]);

  useEffect(() => {
    if (recommendations.loading) {
      return;
    }

    if (!recommendationsInitiallyTriggeredRef.current) {
      // avoid triggering the event when first loading the recommendations
      // with the default tag
      recommendationsInitiallyTriggeredRef.current = true;
      return;
    }

    // trigger the event after recommendations are loaded
    // to get the correct geoLocation
    trackRecommendationTabSelected({
      tag: activeTag,
      source: isDeliveryMode
        ? ESearchSource.DELIVERY_RECOMMENDATIONS
        : ESearchSource.HOMEPAGE_RECOMMENDATIONS,
      geoLocation: recommendations.location || 'USA',
      searcherGeoLocation: myIPAddress || '',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations.loading, activeTag, isDeliveryMode]);

  const onClickRental = (rental: IRentalTile, index: number) => {
    trackListingSelectedEvent({
      rental,
      eventSource: listingSource,
      listingPlacement: index + 1,
      tab: activeTag,
    });
  };

  const onChangeImage = (rental: IRentalTile, index: number, nextIndex: number) => {
    trackListingImageViewedEvent(rental, listingSource, index + 1, nextIndex + 1);
  };

  const getViewAllHref = () => {
    if (isDeliveryMode) {
      return `/rv-search?${stringify({
        [ESearchFilters.DELIVERY]: true,
        [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.STATIONARY,
        ...(recommendations.location && { address: recommendations.location }),
      })}`;
    }

    return `/rv-search?${stringify({
      address: recommendations.location,
    })}`;
  };

  const viewAllHref = getViewAllHref();

  const buildHeading2ndLine = () => {
    if (!isDeliveryMode && activeTag === ETagSlug.ForYou) {
      return <FormattedMessage defaultMessage="for you" id="OJvXWy" />;
    }

    if (recommendations.location)
      return (
        <FormattedMessage
          defaultMessage="near {location}"
          values={{
            location: (
              <Link href={viewAllHref} legacy passHref>
                <a
                  href={viewAllHref}
                  className="underline hover:no-underline underline-offset-4 decoration-2">
                  {recommendations.location}
                </a>
              </Link>
            ),
          }}
          id="MtFB8P"
        />
      );

    return (
      (recommendations.loading && <>&nbsp;</>) || (
        <FormattedMessage defaultMessage="in the country" id="+Gb73f" />
      )
    );
  };

  // To avoid showing the fallback value for the second line of the heading every
  // single time we are loading next set of recommendations, we are going to use
  // a state to store the value of the second line value, and update it only after
  // the recommendations are loaded.
  const [heading2ndLine, setHeading2ndLine] = useState(() => buildHeading2ndLine());

  useEffect(() => {
    if (!recommendations.loading) setHeading2ndLine(buildHeading2ndLine());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations]);

  const handleViewAllClick = () => {
    const eventData = {
      tab: activeTag,
      source: isDeliveryMode ? 'delivery-recommendations' : 'homepage-recommendations',
      geoLocation: recommendations.location || 'USA',
      searcherGeoLocation: myIPAddress || '',
    };

    trackViewAllRentalsClicked(eventData);
  };

  const handleTagChange = (tag: ETagSlug) => {
    setActiveTag(tag);
  };

  const getHeadingContent = () => {
    if (isDeliveryMode) {
      return <FormattedMessage defaultMessage="Find the best stationary RVs" id="+8P8BI" />;
    }

    if (isRentalsVDeliverySearchModeEnabled) {
      return <FormattedMessage defaultMessage="Find the best drivable RVs" id="fCmno0" />;
    }

    return <FormattedMessage defaultMessage="Find the best RV rentals" id="DaGido" />;
  };

  const getDescriptionContent = () => {
    if (isDeliveryMode) {
      return (
        <FormattedMessage defaultMessage="Then kick your feet up. We've got this." id="ieYaWk" />
      );
    }

    if (isRentalsVDeliverySearchModeEnabled) {
      return (
        <FormattedMessage
          defaultMessage="Explore the open road in the perfect RV rental."
          id="/XHuLh"
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="Explore the open road in the perfect RV rental. Or, get one delivered and set up for you."
        id="yq8PYC"
      />
    );
  };

  const getButtonLabel = () => {
    if (isDeliveryMode) {
      return <FormattedMessage defaultMessage="View all stationary RVs" id="hcg97R" />;
    }
    return (
      <FormattedMessage
        defaultMessage="View all<desktop> rentals</desktop>"
        values={{
          desktop: value => (
            <span aria-hidden className="hidden md:inline">
              {value}
            </span>
          ),
        }}
        id="0EFHuC"
      />
    );
  };

  const getGridClassName = () => {
    const baseClasses = `grid gap-4 lg:gap-x-8 lg:grid-cols-3 xxxxl:grid-cols-4 ${css.grid}`;

    if (isDeliveryMode) {
      return `${baseClasses} grid-cols-1`;
    }

    return `${baseClasses} grid-cols-2`;
  };

  return (
    <div className="flex flex-col px-6 mx-auto max-w-xxxxl lg:px-20">
      <div className="flex flex-col gap-4 lg:gap-6 secton-animated-bilock">
        <div>
          <Heading variant={EHeadingStyleVariant.H1Alt} className={css.heading}>
            {getHeadingContent()}
          </Heading>

          {!isDeliveryMode && (
            <Heading component="div" variant={EHeadingStyleVariant.H1Alt} className={css.heading}>
              {heading2ndLine}
            </Heading>
          )}
        </div>

        <Text variant={ETextStyleVariant.LargeRegular} className={css.description}>
          {getDescriptionContent()}
        </Text>
      </div>

      <Tags active={activeTag} onChange={handleTagChange} mode={mode} />

      <div className="flex flex-col gap-4 lg:gap-16 section-animated-block">
        <div className={getGridClassName()}>
          {recommendations.tiles.map((rentalTile, index) => (
            <ModularListingTile
              key={`${activeTag}-${index}`}
              rentalTile={rentalTile}
              target={isAboveDesktop ? '_blank' : undefined}
              onClick={() => onClickRental(rentalTile, index)}>
              <ModularListingTileMedia
                autoRotateOnHover
                rentalTile={rentalTile}
                {...(recommendations.loading
                  ? {}
                  : {
                      largeSourceSize: false,
                      onSlideChange: nextIndex => onChangeImage(rentalTile, index, nextIndex),
                    })}
                className="aspect-square md:aspect-[407/236] !h-auto"
                showSuperhost={superhostBadgeEnabled}
                wishlistingEventData={{
                  index,
                  eventSource: listingSource,
                }}
                showGuestFavorite
                hideTagOnSmallScreen
              />

              <div aria-hidden={!isAboveTablet} className="hidden md:block">
                <ModularListingTileContent rentalTile={rentalTile} />
              </div>

              <div aria-hidden={isAboveTablet} className="md:hidden">
                <ModularListingTileContentShort rentalTile={rentalTile} />
              </div>
            </ModularListingTile>
          ))}
        </div>

        <Link href={viewAllHref} legacy passHref>
          <Button
            variant={EButtonColorVariant.Tertiary}
            label={getButtonLabel()}
            href={viewAllHref}
            className="self-center"
            onClick={handleViewAllClick}
          />
        </Link>
      </div>
    </div>
  );
};
